import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';

interface InitialData {
  certificate_type?: string;
  property_address?: string;
  normalized_address?: string;
  street_address?: string;
  flat_number?: string | string[];
  zipcode?: string;
  issue_date?: string;
  expiry_date?: string;
  inspector_name?: string;
  certificate_number?: string;
  [key: string]: any;
}

interface ManualCertificateEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (certificateId?: string) => void;
  aiExplanation: string;
  fileInfo: {
    filename: string;
    original_filename: string;
    file_path: string;
  };
  /** Pre-fill form fields from extracted PDF data (used when overriding duplicate detection) */
  initialData?: InitialData;
}

const ManualCertificateEntryDialog: React.FC<ManualCertificateEntryDialogProps> = ({
  open,
  onClose,
  onSuccess,
  aiExplanation,
  fileInfo,
  initialData
}) => {
  const { token } = useAuth();

  const buildFormData = (data?: InitialData) => ({
    certificate_type: data?.certificate_type || '',
    property_address: data?.property_address || '',
    normalized_address: data?.normalized_address || '',
    street_address: data?.street_address || '',
    flat_number: Array.isArray(data?.flat_number)
      ? data!.flat_number.join(', ')
      : (data?.flat_number || ''),
    zipcode: data?.zipcode || '',
    issue_date: data?.issue_date || '',
    expiry_date: data?.expiry_date || '',
    inspector_name: data?.inspector_name || '',
    certificate_number: data?.certificate_number || '',
    additional_details: ''
  });

  const [formData, setFormData] = useState(buildFormData(initialData));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-populate form whenever the dialog opens (handles the override case)
  useEffect(() => {
    if (open) {
      setFormData(buildFormData(initialData));
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Convert flat_number to array if provided
      const flatNumbers = formData.flat_number 
        ? formData.flat_number.split(',').map(f => f.trim()).filter(f => f)
        : [];

      const response = await fetch(`${config.apiUrl}/api/manual-certificate-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          flat_number: flatNumbers,
          filename: fileInfo.filename,
          original_filename: fileInfo.original_filename,
          file_path: fileInfo.file_path,
          override_duplicate: !!initialData  // signal to backend to skip duplicate check
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save certificate');
      }

      console.log('📦 Manual certificate entry response:', data);
      const certId = data.certificate?.id;
      if (certId) {
        console.log('✅ Manual entry certificate ID:', certId);
      }
      onSuccess(certId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Manual Certificate Entry
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Context banner */}
        {initialData ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Overriding duplicate detection
            </Typography>
            <Typography variant="body2">
              The system identified this PDF as a duplicate. The fields below are pre-filled with the data extracted from it.
              Review and correct them before saving.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Why automatic extraction failed:
            </Typography>
            <Typography variant="body2">
              {aiExplanation}
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can manually enter the certificate information below. All fields are optional.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Certificate Type"
            value={formData.certificate_type}
            onChange={handleChange('certificate_type')}
            placeholder="e.g., Gas Safety Certificate, EICR, EPC"
            fullWidth
          />

          <TextField
            label="Property Address"
            value={formData.property_address}
            onChange={handleChange('property_address')}
            placeholder="Full address"
            fullWidth
            multiline
            rows={2}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Normalized Address"
              value={formData.normalized_address}
              onChange={handleChange('normalized_address')}
              placeholder="e.g., 123 Main Street"
              fullWidth
            />

            <TextField
              label="Street Address"
              value={formData.street_address}
              onChange={handleChange('street_address')}
              placeholder="Street and city"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Flat/Unit Number"
              value={formData.flat_number}
              onChange={handleChange('flat_number')}
              placeholder="e.g., Flat 2, Flat 3 (comma-separated)"
              fullWidth
            />

            <TextField
              label="Postcode"
              value={formData.zipcode}
              onChange={handleChange('zipcode')}
              placeholder="e.g., SW1A 1AA"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Issue Date"
              value={formData.issue_date}
              onChange={handleChange('issue_date')}
              placeholder="DD/MM/YYYY"
              fullWidth
            />

            <TextField
              label="Expiry Date"
              value={formData.expiry_date}
              onChange={handleChange('expiry_date')}
              placeholder="DD/MM/YYYY"
              fullWidth
            />
          </Box>

          <TextField
            label="Inspector Name"
            value={formData.inspector_name}
            onChange={handleChange('inspector_name')}
            placeholder="Inspector or engineer name"
            fullWidth
          />

          <TextField
            label="Certificate Number"
            value={formData.certificate_number}
            onChange={handleChange('certificate_number')}
            placeholder="Certificate reference number"
            fullWidth
          />

          <TextField
            label="Additional Details"
            value={formData.additional_details}
            onChange={handleChange('additional_details')}
            placeholder="Any other relevant information"
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Saving...' : 'Save Certificate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualCertificateEntryDialog;
